import { useRef } from "react";
import { motion, useReducedMotion, useSpring, useInView } from "framer-motion";

export const EASE_EXPO = [0.76, 0, 0.24, 1];
export const EASE_OUT = [0.22, 1, 0.36, 1];

/**
 * Signature masked line-by-line text reveal.
 * Each line slides up from inside an overflow-hidden mask.
 */
export function MaskedLines({
  lines,
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 0.09,
  as = "h1",
  testid,
}) {
  const reduce = useReducedMotion();
  const Tag = as;
  return (
    <Tag className={className} data-testid={testid}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={reduce ? { opacity: 0 } : { y: "115%" }}
            animate={reduce ? { opacity: 1 } : { y: "0%" }}
            transition={{
              duration: reduce ? 0.4 : 1.3,
              ease: EASE_EXPO,
              delay: delay + i * stagger,
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/** Same masked reveal, but triggered when scrolled into view. */
export function MaskedLinesOnScroll({
  lines,
  className = "",
  lineClassName = "",
  stagger = 0.09,
  as = "h2",
  testid,
}) {
  const reduce = useReducedMotion();
  const Tag = as;
  // Observe the outer (un-clipped) heading element — the inner spans start
  // translated fully outside their overflow-hidden masks, so observing them
  // directly means the IntersectionObserver never fires.
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <Tag ref={ref} className={className} data-testid={testid}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={reduce ? { opacity: 0 } : { y: "115%" }}
            animate={
              reduce
                ? { opacity: inView ? 1 : 0 }
                : { y: inView ? "0%" : "115%" }
            }
            transition={{ duration: reduce ? 0.4 : 1.2, ease: EASE_EXPO, delay: i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/** Fade-and-rise scroll reveal wrapper. */
export function Reveal({ children, className = "", delay = 0, y = 32, testid }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      data-testid={testid}
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.9, ease: EASE_OUT, delay }}
    >
      {children}
    </motion.div>
  );
}

/** Magnetic hover wrapper — element gently pulls toward the cursor. */
export function Magnetic({ children, strength = 0.28 }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useSpring(0, { stiffness: 160, damping: 14, mass: 0.2 });
  const y = useSpring(0, { stiffness: 160, damping: 14, mass: 0.2 });

  if (reduce) return <>{children}</>;

  function onMove(e) {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  }
  function onLeave() {
    x.set(0);
    y.set(0);
  }
  return (
    <motion.div
      ref={ref}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

/** Returns a mouse-move handler that feeds the .spotlight-card CSS vars. */
export function spotlightHandler(e) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
}
